'use client';

import React from 'react';
import { Button } from '@/components/ui/button';
import { H3, P } from '@/components/ui/typography';
import { cn } from '@/lib/shared/ui';
import { Pause, Play } from 'lucide-react';
import Image from 'next/image';

import headerGradientBg from '@/assets/images/header-gradient-bg.jpg';

export const HeaderImage = () => {
  return (
    <div
      className={cn(
        'h-[10rem] w-full rounded-lg bg-linear-to-b from-background-color to-accent/30',
        'bg-cover bg-center bg-no-repeat',
      )}
      style={{ backgroundImage: `url(${headerGradientBg.src})` }}
    ></div>
  );
};

export const HeroVideo = () => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = React.useState(true);

  const togglePlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        setIsPlaying(true);
        videoRef.current.play();
      } else {
        setIsPlaying(false);
        videoRef.current.pause();
      }
    }
  };

  return (
    <div className="relative">
      <div className="p-4 bg-white/20 border border-blue-300 rounded-2xl">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          loop
          muted
          className={cn(
            'w-full overflow-hidden rounded-lg  relative z-10',
            'shadow-[0px_0px_55px_20px_#efb0ef6e]',
          )}
          onClick={() => {
            togglePlay();
          }}
          src="/videos/object-audio/oa-hero-video-fade.mp4"
        />
      </div>
      <Button
        onClick={togglePlay}
        className="rounded-full z-10 absolute bottom-10 right-10 bg-purple-400 text-background-color hover:bg-purple-500 transition-colors border border-purple-100"
        size="icon"
      >
        {isPlaying ? <Pause /> : <Play />}
      </Button>
    </div>
  );
};

export const ImageBlock = ({
  title,
  description,
  image,
  reverse = false,
  video,
}: {
  title: string;
  description: string;
  image?: string;
  reverse?: boolean;
  video?: string;
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  return (
    <div
      className={cn(
        'flex items-center sm:gap-20 gap-10 justify-center sm:flex-row flex-col',
        reverse ? 'sm:flex-row-reverse' : 'sm:flex-row',
      )}
    >
      <div className={cn('flex flex-1', reverse ? 'justify-start' : 'justify-end')}>
        <div className="flex flex-col gap-2 sm:max-w-96">
          <H3 className="">{title}</H3>
          <P className="opacity-80">{description}</P>
        </div>
      </div>
      <div className={cn('flex-1 flex', reverse ? 'justify-end' : 'justify-start')}>
        {image && (
          <Image
            height={1000}
            width={1000}
            src={image}
            alt={title}
            className="w-full max-w-md rounded-lg"
          />
        )}

        {video && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            loop
            muted
            className="w-full max-w-md rounded-lg mt-4"
            onClick={() => {
              // eslint-disable-next-line
              videoRef.current?.paused
                ? videoRef.current?.play()
                : videoRef.current?.pause();
            }}
            src={video}
          />
        )}
      </div>
    </div>
  );
};
